import { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '@/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Gltf, Environment, Float, Center } from '@react-three/drei';
import * as THREE from 'three';

// ==================================================================
// 1. MASTER ÖZELLEŞTİRME PANELİ (KNOXOPIA KUMANDA MERKEZİ)
// ==================================================================
const NAV_SETTINGS = {
  // --- MENÜ GENEL BOYUTLARI VE ARKA PLAN ---
  dimensions: {
    paddingNormal: "py-11",      // Sayfa en üstteyken menü yüksekliği
    paddingScrolled: "py-6",    // Aşağı kayınca menü daralma oranı
    maxWidth: "px-6 md:px-16",  // Kenarlardan uzaklık
    bgOpacity: "bg-black/40",    // Glassmorphism saydamlığı (%40 siyah)
    blurStrength: "backdrop-blur-md", // Arka plan buğusu (Buzlu cam etkisi)
    borderBottom: "border-b border-white/5", // Alt şeffaf çizgi
  },

  // --- 3D LOGO (MERKEZDEKİ OBJE) AYARLARI ---
  logo: {
    src: "/knoxopia_logo_3d.glb", // 3D model dosya yolu
    scaleNormal: 0.35,           // Logonun devasa duruşu (Sayı büyüdükçe büyür)
    scaleScrolled: 0.20,        // Sayfa kayınca logonun küçülme oranı
    yOffsetNormal: "-52%",      // Logonun dikey konumu (Yukarı almak için -60% yap)
    yOffsetScrolled: "-50%",
    canvasSize: "w-32 h-32 md:w-56 md:h-56", // 3D modelin içinde yaşadığı kutu alanı
    rotationSpeed: 0.4,         // Normal dönüş hızı
    hoverRotationSpeed: 2.0,    // Fare üzerindeyken dönüş hızı
  },

  // --- YAZI, FONT VE PARLAMA (GLOW) STİLLERİ ---
  style: {
    fontSize: "12px",             // Menü yazılarının puntosu
    fontFamily: "'JetBrains Mono', monospace", // Siberpunk fontu
    letterSpacing: "0.3em",

    // --- GLOW (PARLAMA) AYARLARI ---
    // textGlow: Harfler karışırken her bir harfin etrafındaki ışık patlaması
    textGlow: (color) => `0 0 8px ${color}`,
    // ultraGlow: Alt neon çizginin güçlü bloom (ışık yayılımı) efekti
    ultraGlow: (color) => `0 0 10px ${color}, 0 0 25px ${color}, 0 0 45px ${color}`,

    underlineHeight: "2px",
    underlineOffset: "8px",
    defaultColor: "#ffffff",      // Yazıların ve çizginin son hali (Beyaz)

    // --- GLITCH (ZAMANLAMA VE RENK) AYARLARI ---
    glitchChars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789%&@#$*",
    glitchColors: ["#ff003c", "#00f0ff", "#bc13fe", "#e6e6e6", "#ffed00"], // Neon paleti
    glitchSpeed: 60,              // Harflerin değişim hızı (Ms)
    resolveSpeed: 0.25,            // Yazının orijinaline dönme hızı
    inactiveOpacity: 0.4,         // Seçili olmayan linklerin sönüklük oranı
  }
};

// --- MENÜ LİSTESİ ---
const menuItems = [
  { label: 'about', href: '#philosophy' },
  { label: 'projects', href: '#projects' },
  { label: 'works', href: '#awards' },
  { label: 'universe', href: '#gallery' },
  { label: 'services', href: '#services' },
  { label: 'contact', href: '#contact' },
];

// ==================================================================
// 2. SİSTEM ÇALIŞTIRICI (Hooklar ve Alt Bileşenler)
// ==================================================================

// Yazı Karıştırma, Harf Glow ve Çizgi Rengi Senkronizasyon Motoru
const useScrambleText = (originalText) => {
  const [charStates, setCharStates] = useState(originalText.split("").map((char) => ({ char, color: "" })));
  const [currentLineColor, setCurrentLineColor] = useState("");
  const intervalRef = useRef(null);

  const scramble = useCallback(() => {
    let iteration = 0;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      // Çizgi rengini her karede rastgele güncelle (Glitch hissi)
      const randomLineColor = NAV_SETTINGS.style.glitchColors[Math.floor(Math.random() * NAV_SETTINGS.style.glitchColors.length)];
      setCurrentLineColor(randomLineColor);

      setCharStates(originalText.split("").map((originalChar, index) => {
        if (index < iteration) return { char: originalChar, color: "" };
        const randomChar = NAV_SETTINGS.style.glitchChars[Math.floor(Math.random() * NAV_SETTINGS.style.glitchChars.length)];
        const randomColor = NAV_SETTINGS.style.glitchColors[Math.floor(Math.random() * NAV_SETTINGS.style.glitchColors.length)];
        return { char: randomChar, color: randomColor };
      }));

      if (iteration >= originalText.length) {
        clearInterval(intervalRef.current);
        setCurrentLineColor("");
      }
      iteration += NAV_SETTINGS.style.resolveSpeed;
    }, NAV_SETTINGS.style.glitchSpeed);
  }, [originalText]);

  const reset = useCallback(() => {
    clearInterval(intervalRef.current);
    setCharStates(originalText.split("").map((char) => ({ char, color: "" })));
    setCurrentLineColor("");
  }, [originalText]);

  useEffect(() => {
    reset();
  }, [originalText, reset]);

  return { charStates, currentLineColor, scramble, reset };
};

// Harf Glow ve Neon Çizgi Bileşeni
const ScrambleNavLink = ({ item, activeSection, onClick, index }) => {
  const { charStates, currentLineColor, scramble, reset } = useScrambleText(item.label);
  const [isHovered, setIsHovered] = useState(false);
  const isActive = activeSection === item.href.replace('#', '');

  const lineVisible = isHovered || isActive;
  const lineActiveColor = currentLineColor || NAV_SETTINGS.style.defaultColor;
  const textDefaultOpacity = isActive ? 1 : NAV_SETTINGS.style.inactiveOpacity;

  return (
    <motion.a
      href={item.href}
      className="relative cursor-pointer py-1 flex flex-col items-center group"
      style={{
        fontSize: NAV_SETTINGS.style.fontSize,
        fontFamily: NAV_SETTINGS.style.fontFamily,
        letterSpacing: NAV_SETTINGS.style.letterSpacing,
        textTransform: "uppercase"
      }}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => { scramble(); setIsHovered(true); }}
      onMouseLeave={() => { reset(); setIsHovered(false); }}
      onClick={(e) => { e.preventDefault(); onClick(item.href); }}
    >
      {/* HARF ALANI: Karışırken her harf kendi renginde parlar */}
      <div className="flex">
        {charStates.map((obj, i) => (
          <span
            key={i}
            style={{
              color: obj.color || NAV_SETTINGS.style.defaultColor,
              opacity: obj.color ? 1 : textDefaultOpacity,
              // YAZI PARLAMASI: Harf renkliyken parlama fonksiyonunu çağırır
              textShadow: obj.color ? NAV_SETTINGS.style.textGlow(obj.color) : "none",
              transition: obj.color ? 'none' : 'all 0.4s ease-out'
            }}
          >
            {obj.char}
          </span>
        ))}
      </div>

      {/* ULTRA GLITCH NEON ÇİZGİ: Harflerle senkronize renk değiştirir */}
      <motion.div
        className="absolute w-full origin-left"
        style={{
          height: NAV_SETTINGS.style.underlineHeight,
          bottom: `-${NAV_SETTINGS.style.underlineOffset}`,
        }}
        animate={{
          scaleX: lineVisible ? 1 : 0,
          backgroundColor: lineActiveColor,
          // Master Panel'deki 3 katmanlı güçlü parlama
          boxShadow: lineVisible ? NAV_SETTINGS.style.ultraGlow(lineActiveColor) : "none",
          opacity: lineVisible ? [1, 0.8, 1, 0.9, 1] : 0
        }}
        transition={{
          scaleX: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
          backgroundColor: { duration: 0.1 },
          opacity: { repeat: Infinity, duration: 1.5, ease: "linear" }
        }}
      />
    </motion.a>
  );
};

// ==================================================================
// 3. ANA NAVİGASYON BİLEŞENİ
// ==================================================================
export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [logoHovered, setLogoHovered] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const { t } = useLanguage();

  // Translated Menu Items
  const translatedMenuItems = menuItems.map(item => ({
    ...item,
    label: t('nav', item.label)
  }));

  // Sayfa kaydırıldığında Glassmorphism efektini tetikleyen takipçi
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = menuItems.map(item => item.href.replace('#', ''));
      for (const section of sections.reverse()) {
        const el = document.getElementById(section);
        if (el && el.getBoundingClientRect().top <= 200) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    if (href === '#projects') {
      navigate('/projects');
      return;
    }
    if (!href.startsWith('#')) {
      navigate(href);
      return;
    }
    // Ana sayfada değilsek: hedefi sakla ve ana sayfaya dön.
    // App.js'deki scrollTo mekanizması bölüm yüklenene kadar (15x) deneyerek kaydırır.
    if (!isHomePage) {
      sessionStorage.setItem('scrollTo', href);
      navigate('/');
      return;
    }
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 w-full z-[9999] transition-all duration-700 ${scrolled ? NAV_SETTINGS.dimensions.paddingScrolled : NAV_SETTINGS.dimensions.paddingNormal
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
    >
      {/* ARKA PLAN: Sadece sayfa aşağı inince Master Panel ayarlarıyla görünür */}
      <motion.div
        className={`absolute inset-0 -z-10 pointer-events-none transition-all duration-500 ${NAV_SETTINGS.dimensions.bgOpacity
          } ${NAV_SETTINGS.dimensions.blurStrength
          } ${scrolled ? NAV_SETTINGS.dimensions.borderBottom : ""
          }`}
        animate={{ opacity: scrolled ? 1 : 0 }}
      />

      <div className={`relative z-50 ${NAV_SETTINGS.dimensions.maxWidth} flex items-center justify-between`}>

        {/* Sol Menü Grubu */}
        <nav className="hidden lg:flex items-center gap-12">
          {translatedMenuItems.slice(0, 3).map((item, index) => (
            <ScrambleNavLink key={item.href} item={item} index={index} activeSection={activeSection} onClick={handleNavClick} />
          ))}
        </nav>

        {/* 3D LOGO: Master Panel'deki konum ve büyüklük ayarlarını kullanır */}
        <motion.a
          href="/"
          className="absolute left-1/2 top-1/2 cursor-pointer z-[100] origin-center"
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
          onClick={(e) => { e.preventDefault(); isHomePage ? window.scrollTo({ top: 0, behavior: 'smooth' }) : navigate('/'); }}
          animate={{ x: "-50%", y: scrolled ? NAV_SETTINGS.logo.yOffsetScrolled : NAV_SETTINGS.logo.yOffsetNormal }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className={`${NAV_SETTINGS.logo.canvasSize} flex items-center justify-center`}>
            <Canvas camera={{ position: [0, 0, 3.5], fov: 40 }} gl={{ alpha: true, antialias: true }}>
              <Suspense fallback={null}>
                <ambientLight intensity={0.5} />
                <Environment preset="studio" />
                <NavModel isHovered={logoHovered} scrolled={scrolled} />
              </Suspense>
            </Canvas>
          </div>
        </motion.a>

        {/* Sağ Menü Grubu */}
        <nav className="hidden lg:flex items-center gap-12">
          {translatedMenuItems.slice(3).map((item, index) => (
            <ScrambleNavLink key={item.href} item={item} index={index + 3} activeSection={activeSection} onClick={handleNavClick} />
          ))}
        </nav>

        {/* Mobil Buton */}
        <div className="lg:hidden flex items-center gap-4 ml-auto z-[200]">
          <button className="text-white ml-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <div className="w-6 h-4 flex flex-col justify-between items-end">
                <span className="w-full h-[2px] bg-white" />
                <span className="w-4 h-[2px] bg-white opacity-60" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Mobil Menü Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[250] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center lg:hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <nav className="flex flex-col items-center gap-10">
              {menuItems.map((item, index) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  className="text-white/70 hover:text-white transition-colors"
                  style={{
                    fontSize: '20px',
                    fontFamily: NAV_SETTINGS.style.fontFamily,
                    letterSpacing: NAV_SETTINGS.style.letterSpacing,
                    textTransform: 'uppercase',
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.07 }}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                >
                  {t('nav', item.label)}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

// ==================================================================
// 4. 3D MODEL MOTORU (AÇIKLAMALI)
// ==================================================================
function NavModel({ isHovered, scrolled }) {
  const groupRef = useRef();

  // useFrame: Her karede (FPS) çalışan 3D motoru
  useFrame((state, delta) => {
    if (!groupRef.current) return;

    // DÖNÜŞ: Fare üzerindeyse Master Panel'deki hızlı hızı, değilse yavaş hızı kullan
    const rotationSpeed = isHovered ? NAV_SETTINGS.logo.hoverRotationSpeed : NAV_SETTINGS.logo.rotationSpeed;
    groupRef.current.rotation.y += delta * rotationSpeed;

    // ÖLÇEKLENME (Lerp): Normal ve Kaydırılmış ölçekler arasında yumuşak geçiş yapar
    const targetScale = scrolled ? NAV_SETTINGS.logo.scaleScrolled : NAV_SETTINGS.logo.scaleNormal;
    const smoothScale = THREE.MathUtils.lerp(groupRef.current.scale.x, targetScale, 0.08);
    groupRef.current.scale.set(smoothScale, smoothScale, smoothScale);
  });

  return (
    // Float: Modele havada süzülme (yüzme) etkisi verir
    <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.2}>
      <group ref={groupRef}>
        <Center>
          <Gltf
            src={NAV_SETTINGS.logo.src}
            onLoad={(gltf) => {
              // Blender'dan gelen modelin materyal özelliklerini kodla güçlendiriyoruz
              gltf.scene.traverse((child) => {
                if (child.isMesh) {
                  child.material.metalness = 1.0; // %100 Metalik doku
                  child.material.roughness = 0.1; // Pürüzsüz, ayna gibi yansıma
                  child.material.envMapIntensity = 2.5; // Çevre ışığının parlama gücü
                }
              });
            }}
          />
        </Center>
      </group>
    </Float>
  );
}